import type { FC } from 'react'
import { useMemo, useState } from 'react'

import { Input, LoaderCircle, Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useQuery } from '@tanstack/react-query'
import PlusIcon from 'mdi-react/PlusIcon'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useAdminAccess } from '@/hooks/access'
import { useSearch } from '@/hooks/searchParams'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import { UserForm } from '@/modules/admin/users/UserForm'
import { APP_ROUTES } from '@/util/app-routes'

import { UserList } from './UserList'

import './Users.styles.scss'

const PAST_CUSTOMER_STATUS_ID = 2
const FOR_APPROVAL_STATUS_ID = 3

export const Users: FC = () => {
  useAdminAccess()
  const translate = useTranslation()
  const { searchFilter, setSearchFilter } = useSearch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isOpen, setIsOpen] = useState(false)

  const navigate = useNavigate()

  const userId = Number(searchParams.get('id'))

  const onEdit = (id: number) => {
    setSearchParams(prevSearchParams => {
      prevSearchParams.set('id', String(id))
      return prevSearchParams
    })
    setIsOpen(true)
  }

  const onClose = () => {
    setSearchParams(prevSearchParams => {
      prevSearchParams.delete('id')
      return prevSearchParams
    })
    setIsOpen(false)
  }

  const {
    data: users = [],
    isSuccess,
    isLoading,
  } = useQuery(queries.adminUsersList(), adminResources.getUsersList)

  const { data: customers } = useQuery(
    queries.adminUserCustomersList(),
    adminResources.getUserCustomersList
  )

  const customerOptions = useMemo(
    () =>
      customers
        ?.filter(
          customer =>
            ![PAST_CUSTOMER_STATUS_ID, FOR_APPROVAL_STATUS_ID].includes(customer.customerStatus)
        )
        .map(customer => ({
          label: customer.name,
          value: customer.id,
        })) || [],
    [customers]
  )

  if (isLoading) {
    return (
      <Container className="text-templates">
        <LoaderCircle />
      </Container>
    )
  }

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.HOME)} className="admin-users">
      <Container.Header>
        <Translation id="Users" />
      </Container.Header>
      <div className="mt-4">
        <Button
          className="mb-4 ml-0"
          Icon={<PlusIcon />}
          disabled={!isSuccess}
          onClick={() => setIsOpen(true)}
        >
          <Translation id="New User" />
        </Button>

        <Input
          id="searchFilter"
          type="text"
          value={searchFilter}
          placeholder={translate('Search')}
          onChange={e => setSearchFilter(e.target.value)}
        />
      </div>
      <UserList users={users} searchFilter={searchFilter} onEdit={onEdit} />
      <Popup
        className="w-2/3"
        ariaLabel={translate('Add or edit user')}
        isOpen={isOpen || !!userId}
        onClose={onClose}
      >
        <UserForm onClose={onClose} userId={userId} customers={customerOptions} />
      </Popup>
    </Container>
  )
}
